import {
  ResponsesFeeBasedContractResponse,
  ResponsesFeeBasedTemplateResponse,
  ResponsesFixedFeeAgreementResponse,
  ResponsesFixedFeeConsolidatedPositionResponse,
  ResponsesValidateFixedFeeAgreementResponse,
} from '@genial/services/dist/models';
import { Utils } from '@genial/utils';

import { mountAccount } from '../customer/normalize';

import {
  fixedFeeStatusIcons,
  FixedFeeListStatus,
  statusAbleToReplace,
  statusAbleToCancel,
  statusAbleToResendNotification,
} from './constants';
import {
  FixedFeeCustomerNetWorth,
  FixedFeeNewContractTerm,
  FixedFeeTermHistoryItem,
  FixedFeeCustomerTermReplacementStatus,
  FixedFeeTerm,
  FixedFeeTermDetailed,
} from './types';

const getStatusIconAndColor = (statusId: number, type: 'list' | 'notification' = 'list') => {
  if (fixedFeeStatusIcons[type][statusId]) {
    return fixedFeeStatusIcons[type][statusId];
  }
  return fixedFeeStatusIcons[type][0];
};

const normalizeRelatedTerm = (
  term: ResponsesFeeBasedContractResponse
): FixedFeeTerm['relatedTerm'] => {
  if (!term.contractPrevious) {
    return undefined;
  }

  const statusIcon = getStatusIconAndColor(term.contractPrevious.statusId);

  return {
    id:
      term.contractPrevious?.feeBasedContractHash ||
      term.contractPrevious?.feeBasedContractId?.toString() ||
      '',
    status: term.contractPrevious?.status || '',
    statusIcon: statusIcon.icon,
    statusColor: statusIcon.color,
    term: term.contractPrevious?.feeBasedContractHash || '',
    contractDateNormalized: term.contractPrevious?.acceptanceDate
      ? Utils.Mask.DateFormat(term.contractPrevious.acceptanceDate, 'dd/MM/yyyy')
      : undefined,
  };
};

export const getActionsAvailability = (statusId: number, minimumStayDate?: Date | null) => {
  const isActiveStatus = statusId === FixedFeeListStatus.Active;
  const hasMinimumStayEnded = !minimumStayDate || !Utils.Date.IsFuture(minimumStayDate);

  const activeWithMinimumStayEnded = isActiveStatus && hasMinimumStayEnded;
  const activeOk = !isActiveStatus || activeWithMinimumStayEnded;

  return {
    activeWithMinimumStayEnded,
    isAbleToCancel: statusAbleToCancel.includes(statusId) && activeOk,
    isAbleToReplace: statusAbleToReplace.includes(statusId) && activeOk,
    isAbleToResendNotification: statusAbleToResendNotification.includes(statusId),
  };
};

export const normalizeFixedFeeTerm = (term: ResponsesFeeBasedContractResponse): FixedFeeTerm => {
  const statusIcon = getStatusIconAndColor(term.statusId);

  const {
    isAbleToCancel,
    isAbleToReplace,
    isAbleToResendNotification,
    activeWithMinimumStayEnded,
  } = getActionsAvailability(term.statusId, term.minimumStayDate);

  return {
    ...term,
    id: term.feeBasedContractHash || term.feeBasedContractId.toString(),
    gracePeriodEndDateNormalized: term.minimumStayDate
      ? Utils.Mask.DateFormat(term.minimumStayDate, 'dd/MM/yyyy')
      : '',
    term: term.feeBasedContractHash || '',
    status: term.status || '',
    statusIcon: statusIcon.icon,
    statusColor: statusIcon.color,
    customerName: term.customerName || '',
    account: mountAccount(term.spnpAccountNumberWithoutDigit || 0, term.spnpAccountDigit),
    feeRate: `${Utils.Mask.Percentage(term.feePercentage)} a.a`,
    equityExclusion: term.isRvExclusion ? 'Sim' : 'Não',
    createDateNormalized: term.createDate
      ? Utils.Mask.DateFormat(term.createDate, 'dd/MM/yyyy')
      : '',
    contractDateNormalized: term.acceptanceDate
      ? Utils.Mask.DateFormat(term.acceptanceDate, 'dd/MM/yyyy')
      : '',
    waitingResponse: term.statusId === FixedFeeListStatus.Pending && !term.acceptanceDate,
    netWorth: Utils.Mask.Money(term.positionTotalValueInvested),
    eventType: term.event || '--',
    relatedTerm: normalizeRelatedTerm(term),
    isAbleToCancel,
    isAbleToReplace,
    isAbleToResendNotification,
    activeWithMinimumStayEnded,
  };
};

export const normalizeFixedFeeTerms = (
  response: ResponsesFeeBasedContractResponse[] | null | undefined
): FixedFeeTerm[] => {
  return (response || []).map(normalizeFixedFeeTerm);
};

export const normalizeFixedFeeTermDetails = ({
  fixedFeeAgreement,
}: ResponsesFixedFeeAgreementResponse): FixedFeeTermDetailed => {
  const lastUpdatedIcon = getStatusIconAndColor(fixedFeeAgreement.status.statusId, 'notification');
  return {
    ...fixedFeeAgreement,
    statusColor: lastUpdatedIcon.color,
    statusIcon: lastUpdatedIcon.icon,
    statusText: lastUpdatedIcon.description || fixedFeeAgreement.status.status || '',
    updatedDateNormalized: fixedFeeAgreement.status.statusUpdatedAt
      ? Utils.Mask.DateFormat(fixedFeeAgreement.status.statusUpdatedAt, 'dd/MM/yyyy HH:mm')
      : '--',
  };
};

export const normalizeFixedFeeCustomerNetWorth = (
  response: ResponsesFixedFeeConsolidatedPositionResponse | null | undefined
): FixedFeeCustomerNetWorth | undefined => {
  if (!response) {
    return undefined;
  }

  return {
    accountNumber: response.accountNumber,
    totalInvested: response.totalValueInvestedWithoutProductsExcluded,
  };
};

export const normalizeFixedFeeCustomerTermReplacementStatus = (
  response: ResponsesValidateFixedFeeAgreementResponse | null | undefined
): FixedFeeCustomerTermReplacementStatus => {
  if (!response) {
    return {
      canReplace: false,
      canCreate: true,
    };
  }

  return {
    canReplace: response.isSubstitution && response.canCreate,
    canCreate: response.canCreate,
  };
};

export const normalizeFixedFeeNewContractTerm = (
  response: ResponsesFeeBasedTemplateResponse | null | undefined
): FixedFeeNewContractTerm | undefined => {
  if (!response) {
    return undefined;
  }

  return { htmlContent: response.content || '' };
};

export const normalizeFixedFeeTermHistory = (
  response: any[] | null | undefined
): FixedFeeTermHistoryItem[] => {
  return response || [];
};
